import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ASSETS, CLINIC } from "@/data/site";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate("/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Login failed.";
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-xl border border-soft-border bg-white p-4 font-sans text-sm text-charcoal outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/30";

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blush via-cream to-background p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src={ASSETS.logo} alt="Clinic logo" className="h-20 w-20 rounded-full object-contain" />
          <h1 className="mt-4 font-serif text-2xl text-charcoal">Admin Portal</h1>
          <p className="mt-1 text-sm text-taupe">{CLINIC.clinicName}</p>
        </div>
        <form data-testid="admin-login-form" onSubmit={submit} className="rounded-3xl border border-soft-border bg-cream p-8 shadow-card">
          <div className="space-y-5">
            <div>
              <label htmlFor="aemail" className="mb-2 block text-sm font-medium text-charcoal">Email</label>
              <input id="aemail" type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@clinic.com" required />
            </div>
            <div>
              <label htmlFor="apass" className="mb-2 block text-sm font-medium text-charcoal">Password</label>
              <input id="apass" type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" required />
            </div>
          </div>
          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-destructive">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-cream transition-colors hover:bg-terracotta-dark disabled:opacity-70">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
